import React, { useContext, useEffect, useState} from "react";
import { DataGrid } from "@mui/x-data-grid";
import { 
  Button, 
  Tooltip, 
  IconButton, 
  useMediaQuery, 
  useTheme,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Divider
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import NewServiceForm from "./NewService.js"; 
import { getClinicalServices, getPrimaryClinicalServices } from "../API/FacilitySettingsApi";
import AuthContext from "../Context/AuthProvider";
import { CloseOutlined, CreateOutlined } from "@mui/icons-material";

const sampleData = [
  { id: 1, name: "Service 1", duration: 30, fees: 500 },
  { id: 2, name: "Service 2", duration: 60, fees: 1000 },
];

function FacilityServices({ onNewService, onRowClick, onEditDetails }) {
  const [pageSize, setPageSize] = useState(5);
  const [services, setServices] = useState(sampleData);
  const [clinicalServices, setClinicalServices] = useState([]);
  const {auth} = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const columns = [
    { field: "code", headerName: "#", width: 80 },
    { field: "name", headerName: "Name", width: 200 },
    { field: "durationInMins", headerName: "Duration (Mins)", width: 140 },
    { field: "fees", headerName: "Fees (INR)", width: 100 },
    { field: "isActive", headerName: "Is Active", width: 140, type: "boolean" },
    { field: "canBePrimary", headerName: "Can Be Primary", width: 140, type: "boolean" },
    { field: "allowFeesEdit", headerName: "Allow Feed Edit", width: 140, type: "boolean" },


    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Grid2 sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit Service" arrow>
            <IconButton
              variant="outlined"
              onClick={() => onEditDetails(params.row)}
            >
              <CreateOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove Service" arrow>
            <IconButton
              variant="outlined"
              onClick={() => onRemoveService(params.row)}
            >
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid2>
      ),
    },
  ];

  const onRemoveService = (serviceInstance) => {

  }

  useEffect(() => {
          const loadServices = async () => {
            const result = await getPrimaryClinicalServices(auth); // Call the API to fetch the services
            if (result?.isSuccess) {
              setClinicalServices(result?.data); // Set the services data in the state
            } else {
              console.error(result?.message || "Failed to load services");
            }
          };
          loadServices();
        }, [auth]); 

  const handleAddService = (newService) => {
    setServices((prevServices) => [...prevServices, newService]);
  };

  const renderCardView = () => {
    return (
      <Grid2 sx={{ px: 1, maxHeight: "calc(100vh - 150px)", overflowY: "auto"  }}>
        {clinicalServices.length === 0 ? (
          <Typography variant="body1" align="center" py={3}>
            There are no services to display.
          </Typography>
        ) : (
          clinicalServices.map((service) => (
            <Card
              key={service.id}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 2 }}
              onClick={() => onRowClick ? onRowClick({ row: service }) : null}
            >
              <CardContent sx={{ padding: 2, borderRadius: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                  {service.name || ""}
                </Typography>
                
                <Divider sx={{ my: 1 }} />
                
                 {/* <Typography variant="body2" gutterBottom>Code: {service.code || "-"}</Typography> */}
  <Typography variant="body2" gutterBottom>Duration: {service.durationInMins || "-"} mins</Typography>
  <Typography variant="body2" gutterBottom>Fees: ₹ {service.fees || "-"}</Typography>
  <Typography variant="body2" gutterBottom>Is Active: {service.isActive ? "Yes" : "No"}</Typography>
  <Typography variant="body2" gutterBottom>Can Be Primary: {service.canBePrimary ? "Yes" : "No"}</Typography>
  <Typography variant="body2">Allow Fees Edit: {service.allowFeesEdit ? "Yes" : "No"}</Typography>
                
                <CardActions sx={{ justifyContent: "flex-end"}}>
                  <Tooltip title="Edit Service">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditDetails(service);
                      }}
                      size="small"
                    >
                      <CreateOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove Service">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveService(service);
                      }}
                      size="small"
                    >
                      <CloseOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </CardContent>
            </Card>
          ))
        )}
      </Grid2>
    );
  };

  return (
    <Grid2 container>
      <Grid2 container size={12} sx={{ marginX: "5px", marginY: "10px",  display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        
        <Grid2 container size={12} spacing={2} sx={{ alignItems: "center" }}>
          <Tooltip title="New Service" arrow>
            <Button
              variant="contained"
              color="secondary"
              onClick={onNewService}
              size={isMobile ? "small" : "medium"}
              startIcon={<AddRoundedIcon fontSize="small" />}
            >
              New Service
            </Button>
          </Tooltip>
        </Grid2>
      </Grid2>

      {isMobile ? (
        renderCardView()
      ) : (
        <Grid2 size={12} sx={{ marginX: "5px",  maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
          <DataGrid
            sx={{
              "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", fontWeight: "bold" },
              "& .MuiDataGrid-footerContainer": { justifyContent: "space-between" },
            }}
            initialState={{
              density: "compact",
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            rows={clinicalServices}
            columns={columns}
            pageSizeOptions={[5, 10, 15]}
            rowsPerPageOptions={[5, 10, 15]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            onRowClick={onRowClick}
          />
        </Grid2>
      )}
    </Grid2>
  );
}

export default FacilityServices;